import type { Zone } from '../../zones/types'

/**
 * Formats zone information as HTML for popup display.
 */
export const formatZoneTooltip = (zone: Zone): string => {
  const getRiskLevel = (score: number) => {
    if (score >= 5) return { label: 'High Risk', color: '#ef4444' }
    if (score >= 3) return { label: 'Medium Risk', color: '#f97316' }
    return { label: 'Low Risk', color: '#22c55e' }
  }

  const risk = getRiskLevel(zone.score ?? 0)
  const formatArea = (squareMeters: number) => {
    if (squareMeters >= 1000000)
      return `${(squareMeters / 1000000).toFixed(2)} km²`
    if (squareMeters >= 10000) return `${(squareMeters / 10000).toFixed(2)} ha`
    return `${squareMeters.toFixed(0)} m²`
  }

  return `
    <div style="min-width: 200px; max-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
      <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
        ${zone.name ?? 'Unnamed Zone'}
      </div>
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background-color: ${risk.color};"></span>
        <span style="font-size: 12px; color: #6b7280;">${risk.label}</span>
      </div>
      <div style="font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px;">
        Score: ${zone.score ?? 0}/6
      </div>
      <div style="font-size: 11px; color: #6b7280; line-height: 1.5;">
        <div>Area: ${formatArea(zone.area ?? 0)}</div>
      </div>
    </div>
  `
}

/**
 * Convert coordinates from frontend format [lat, lng] to GeoJSON format [lng, lat]
 */
export const convertToGeoJSON = (coordinates: number[][]): number[][] => {
  return coordinates.map(coord => [coord[1]!, coord[0]!])
}

/**
 * Convert coordinates from GeoJSON format [lng, lat] to frontend format [lat, lng]
 */
export const convertFromGeoJSON = (coordinates: number[][]): number[][] => {
  return coordinates
    .filter(
      coord =>
        coord && coord.length >= 2 && coord[0] != null && coord[1] != null
    )
    .map(coord => [coord[1]!, coord[0]!])
}

/**
 * Ensure polygon is closed (first and last coordinates are the same)
 */
export const ensurePolygonClosed = (coordinates: number[][]): number[][] => {
  if (coordinates.length === 0) return coordinates
  const first = coordinates[0]
  const last = coordinates[coordinates.length - 1]
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    return [...coordinates, [first[0]!, first[1]!]]
  }
  return coordinates
}

/**
 * Generate single grid cell polygon from center position and size
 * @param center - Center position [lng, lat] in GeoJSON format
 * @param size - Grid size in kilometers
 * @returns GeoJSON Feature representing the grid cell
 */
export const generateGridCell = (
  center: [number, number],
  size: number
): GeoJSON.Feature => {
  // Calculate half-size in degrees (approximate conversion from km)
  // 1 degree latitude ≈ 111 km
  // 1 degree longitude ≈ 111 km * cos(latitude)
  const lat = center[1]
  const halfSizeLat = size / 2 / 111 // Half size in degrees (latitude)
  const halfSizeLng = size / 2 / (111 * Math.cos((lat * Math.PI) / 180)) // Half size in degrees (longitude)

  // Create square polygon centered at the given position
  const coordinates: [number, number][] = [
    [center[0] - halfSizeLng, center[1] - halfSizeLat], // Bottom-left
    [center[0] + halfSizeLng, center[1] - halfSizeLat], // Bottom-right
    [center[0] + halfSizeLng, center[1] + halfSizeLat], // Top-right
    [center[0] - halfSizeLng, center[1] + halfSizeLat], // Top-left
    [center[0] - halfSizeLng, center[1] - halfSizeLat], // Close polygon
  ]

  return {
    type: 'Feature',
    id: 'grid-cell',
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
    properties: {},
  }
}
